import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation, computed, input, output } from '@angular/core';
import {
  MessageButtonType,
  MessageStateType,
  MessageButton,
  MessageButtonClickEvent,
} from '../../../interfaces';
import { MessageTimeComponent, MessageMapComponent, MessageSenderComponent } from '../../atoms';
import { MessageTextComponent } from '../message-text/message-text.component';
import {
  faArrowUpRightFromSquare,
  faCopy,
  faEllipsis,
  faExclamation,
  faFileAudio,
  faImage,
  faLocationDot,
  faRefresh,
  faPlay,
} from '@fortawesome/free-solid-svg-icons';
import { AssetResponse, Location, MessageKind, ParticipantKind } from '@clientwire/api';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent, FaIconComponent } from '@akostm/orbit-ui';
import { MessageStateIndicatorComponent } from '../message-state-indicator/message-state-indicator.component';
import { WindowService } from '../../../services';
import { faImageSlash, faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';

export const IsTyping = {
  IsTyping: 'IS-TYPING',
};
export const ChatBubbleMessageKind = { ...MessageKind, ...IsTyping };
export type ChatBubbleMessageKind =
  (typeof ChatBubbleMessageKind)[keyof typeof ChatBubbleMessageKind];

@Component({
  selector: 'shared-chat-bubble',
  standalone: true,
  imports: [
    CommonModule,
    MessageTextComponent,
    MessageMapComponent,
    MessageTimeComponent,
    MessageSenderComponent,
    ButtonComponent,
    FaIconComponent,
    TranslatePipe,
    MessageStateIndicatorComponent,
  ],
  templateUrl: './chat-bubble.component.html',
  styleUrl: './chat-bubble.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class ChatBubbleComponent {
  readonly messageKind = input.required<ChatBubbleMessageKind>();
  readonly messageId = input.required<string>();
  readonly mapCenterCoordinates = input<Location>();
  readonly time = input<Date>();
  readonly messageSequence = input<number | null>();
  readonly messageSourceId = input<string | null>();
  readonly messageText = input<string | null>();
  readonly formattedAddress = input<string | null>();
  readonly creatorParticipantId = input<string>();
  readonly userParticipantId = input<string>();
  readonly asset = input<AssetResponse | null>();
  readonly buttons = input<MessageButton[]>();
  readonly conversationAppType = input.required<'main' | 'embed' | 'client'>();
  readonly assetClick = output<string>();
  readonly buttonClick = output<MessageButtonClickEvent>();
  readonly isUnread = input<boolean>(false);
  readonly isTyping = input<boolean>(false);
  readonly showCreator = input<string>();
  readonly lastSeenMessageSequence = input<number>();
  readonly sharedUserDisplayName = input<string | undefined | null>();
  readonly creatorKind = input<ParticipantKind>();
  readonly ariaLabel = input<string>();
  readonly errorIcon = faExclamation;
  readonly refreshIcon = faRefresh;
  readonly copyIcon = faCopy;
  readonly messageButtonType = MessageButtonType;
  readonly messageIdsWithError = input<string[]>();
  readonly windowService = input<WindowService>();
  readonly messageHasError = computed(() => this.messageIdsWithError()?.includes(this.messageId()));
  readonly resendMessage = output<string>();
  readonly messageState = computed(() => {
    if (this.messageId()?.startsWith('new-')) {
      return MessageStateType.Sending;
    } else {
      return (this.lastSeenMessageSequence() ?? 0) < (this.messageSequence() ?? 0)
        ? MessageStateType.Sent
        : MessageStateType.Read;
    }
  });
  readonly debug = false;
  readonly ownerOfMessage = computed(
    () =>
      this.creatorParticipantId() &&
      this.userParticipantId() &&
      this.creatorParticipantId() === this.userParticipantId()
  );

  readonly isSharedUsername = computed(
    () =>
      (this.conversationAppType() === 'main' || this.conversationAppType() === 'embed') &&
      !!this.sharedUserDisplayName() &&
      (this.creatorKind() === ParticipantKind.User ||
        this.creatorKind() === ParticipantKind.Workflow)
  );

  readonly bubblePosition = computed(() =>
    this.ownerOfMessage() || this.isSharedUsername() ? 'right' : 'left'
  );

  readonly thumbnailUrl = computed(() => {
    const thumbnailUrl = this.asset()?.thumbnailUrl;
    return thumbnailUrl ? `url(${thumbnailUrl})` : '';
  });

  readonly messageKindEnum = MessageKind;
  readonly typingIcon = faEllipsis;
  readonly imageIcon = faImage;
  readonly audioIcon = faFileAudio;
  readonly videoIcon = faPlay;
  readonly thumbnailPlaceholderIcon = faArrowUpRightFromSquare;
  readonly locationIcon = faLocationDot;
  readonly loadingSpinnerIcon = faSpinnerThird;
  readonly missingImageIcon = faImageSlash;
  protected readonly MessageButtonType = MessageButtonType;

  onAssetClickKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.assetClick.emit(this.messageId());
    }
  }

  onLocationClickKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.buttonClick.emit({
        type: this.messageButtonType.OpenGoogleMaps,
        data: this.mapCenterCoordinates(),
      });
    }
  }
}
